import { BACKUP_SOURCE_STATUSES, BackupSource, BackupSourceStatus, BackupSourceType } from '../models/BackupSource';

export const OFFICIAL_STORE_PREFERRED_VALUE = 'official_store';

const isEditableBackupSource = (entry: unknown): entry is BackupSource => {
  if (typeof entry !== 'object' || entry === null) {
    return false;
  }
  const candidateBackupSource = entry as Record<string, unknown>;
  const hasKnownSource = Object.values(BackupSourceType).includes(candidateBackupSource['source'] as BackupSourceType);
  const hasUrlString = typeof candidateBackupSource['url'] === 'string';
  return hasKnownSource && hasUrlString;
};

// Coerce any persisted status to a supported value; unknown/malformed statuses become not-scraped (null)
// so the editor never re-serializes a status outside the supported set on save.
const toBackupSourceStatus = (status: unknown): BackupSourceStatus | null =>
  typeof status === 'string' && (BACKUP_SOURCE_STATUSES as readonly string[]).includes(status) ? (status as BackupSourceStatus) : null;

// Lenient parse for the OMS editor: keeps rows with a known source even while the url is still being typed (empty).
// The scrape job's status is preserved as-is so the editor can show it.
export const parseSourcingBackupLinks = (rawSourcingBackupLinks?: string): BackupSource[] => {
  if (!rawSourcingBackupLinks || rawSourcingBackupLinks.trim() === '') {
    return [];
  }

  try {
    const parsedEntries: unknown = JSON.parse(rawSourcingBackupLinks);
    if (!Array.isArray(parsedEntries)) {
      return [];
    }
    return parsedEntries.filter(isEditableBackupSource).map((backupSource) => ({
      source: backupSource.source,
      url: backupSource.url,
      status: toBackupSourceStatus(backupSource.status),
    }));
  } catch {
    return [];
  }
};

export const serializeSourcingBackupLinks = (backupSources: BackupSource[]): string => JSON.stringify(backupSources);

// Official store urls are bookkeeping only (not scraped) and stored as a plain list of url strings.
export const parseOfficialStoreUrls = (rawOfficialStoreUrls?: string): string[] => {
  if (!rawOfficialStoreUrls || rawOfficialStoreUrls.trim() === '') {
    return [];
  }

  try {
    const parsedEntries: unknown = JSON.parse(rawOfficialStoreUrls);
    if (!Array.isArray(parsedEntries)) {
      return [];
    }
    return parsedEntries.filter((entry): entry is string => typeof entry === 'string');
  } catch {
    return [];
  }
};

export const serializeOfficialStoreUrls = (officialStoreUrls: string[]): string => JSON.stringify(officialStoreUrls);

export interface NormalizedProductSourcing {
  backupSources: string;
  officialStoreUrls: string;
  preferredSource: string;
}

// Save-time cleanup: backups are Coupang/Naver (scraped), official store urls are bookkeeping.
// Drops empty/duplicate entries (and a Coupang backup equal to the main cp_url), preserving each kept
// entry's scrape status. Preferred reverts to "no preference" when its channel has no usable link
// (cp_url or a Coupang backup, a Naver backup, or an official store url).
export const normalizeProductSourcing = (
  backupSourcesJson: string | undefined,
  officialStoreUrlsJson: string | undefined,
  preferredSource: string | undefined,
  mainCoupangUrl: string | undefined,
): NormalizedProductSourcing => {
  const trimmedMainCoupangUrl = mainCoupangUrl?.trim() ?? '';

  const seenBackupKeys = new Set<string>();
  const cleanedBackupSources: BackupSource[] = [];
  for (const backupSource of parseSourcingBackupLinks(backupSourcesJson)) {
    const trimmedUrl = backupSource.url.trim();
    const isDuplicateOfMainCoupang = backupSource.source === BackupSourceType.COUPANG && trimmedUrl === trimmedMainCoupangUrl;
    const backupKey = `${backupSource.source}|${trimmedUrl}`;
    if (trimmedUrl !== '' && !isDuplicateOfMainCoupang && !seenBackupKeys.has(backupKey)) {
      seenBackupKeys.add(backupKey);
      cleanedBackupSources.push({ source: backupSource.source, url: trimmedUrl, status: backupSource.status ?? null });
    }
  }

  const seenOfficialStoreUrls = new Set<string>();
  const cleanedOfficialStoreUrls: string[] = [];
  for (const officialStoreUrl of parseOfficialStoreUrls(officialStoreUrlsJson)) {
    const trimmedUrl = officialStoreUrl.trim();
    if (trimmedUrl !== '' && !seenOfficialStoreUrls.has(trimmedUrl)) {
      seenOfficialStoreUrls.add(trimmedUrl);
      cleanedOfficialStoreUrls.push(trimmedUrl);
    }
  }

  const availablePreferredSources = new Set<string>();
  if (trimmedMainCoupangUrl !== '' || cleanedBackupSources.some((backupSource) => backupSource.source === BackupSourceType.COUPANG)) {
    availablePreferredSources.add(BackupSourceType.COUPANG);
  }
  if (cleanedBackupSources.some((backupSource) => backupSource.source === BackupSourceType.NAVER)) {
    availablePreferredSources.add(BackupSourceType.NAVER);
  }
  if (cleanedOfficialStoreUrls.length > 0) {
    availablePreferredSources.add(OFFICIAL_STORE_PREFERRED_VALUE);
  }

  return {
    backupSources: serializeSourcingBackupLinks(cleanedBackupSources),
    officialStoreUrls: serializeOfficialStoreUrls(cleanedOfficialStoreUrls),
    preferredSource: preferredSource && availablePreferredSources.has(preferredSource) ? preferredSource : '',
  };
};
